import * as React from 'react';
import {
  mergeStyles,
  IStyleFunctionOrObject,
  ITheme,
  IStyle,
  styled,
  concatStyleSetsWithProps
} from 'office-ui-fabric-react';

export const componentSpaceTestId = 'componentSpaceTestId';

export interface BackplateComponentSpaceProps {
  /**
   * Elements to render as BusinessBar and ComponentPart experiences of component ux
   */
  children?: React.ReactNode;

  styles?: IStyleFunctionOrObject<BackplateStyleProps, BackplateComponentSpaceStyles>;

  theme?: ITheme;
}

export interface BackplateComponentSpaceStyles {
  root?: IStyle;
}

interface BackplateStyleProps extends Pick<BackplateComponentSpaceProps, 'theme'> {}

/**
 * Version of the space where the component will show up which wraps the
 * component in an element w/ rounded edges and creates some space around it
 * (the backplate).
 *
 * NOTE: Currently, the backplate ALWAYS has OVERFLOW HIDDEN
 *
 * TODO:
 * Task #4398283: Determine scroll capability on the component
 * Task #4398291: Negotiate who handles scroll when component overflows bounds
 */
export const BackplateComponentSpace = React.memo(
  styled((props: BackplateComponentSpaceProps) => {
    const componentSpaceClass = mergeStyles(concatStyleSetsWithProps(props, props.styles).root);

    return (
      <div className={componentSpaceClass} data-testid={componentSpaceTestId}>
        {props.children}
      </div>
    );
  }, getDefaultStyle)
);

function getDefaultStyle(props: BackplateStyleProps): BackplateComponentSpaceStyles {
  const { theme } = props;

  return {
    root: {
      // In this design, the space above the component comes from the bottom
      // padding of the element above it.
      margin: '0px 16px 16px 16px',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      borderRadius: '4px',
      overflow: 'hidden', // allows the border radius to clip the contents
      boxShadow: shadowDepth2,
      backgroundColor: theme ? theme.semanticColors.bodyBackground : 'white', // reset background color for component area
      selectors: {
        ':hover': {
          boxShadow: shadowDepth4
        }
      }
    }
  };
}

// From the figma spec.  These will likely come be supplied by fluent in the future.
const shadowDepth2 = '0 1.6px 3.6px rgba(0, 0, 0, 0.132), 0 0 2px rgba(0, 0, 0, 0.12)';
const shadowDepth4 = '0 6.4px 14.4px rgba(0, 0, 0, 0.132), 0 0 2px rgba(0, 0, 0, 0.12)';
